import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

export interface FrameworkChangeSet {
  id: string;
  status: 'open' | 'submitted' | 'approved' | 'rejected' | 'discarded';
  title: string | null;
  description: string | null;
  createdBy: string;
  createdAt: string;
  submittedAt: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  rejectionReason: string | null;
  appliedAt: string | null;
}

export interface FrameworkChangeItem {
  id: string;
  changeSetId: string;
  changeType: 'create' | 'update' | 'delete';
  entityType: string;
  entityId: string | null;
  beforeJson: string | null;
  afterJson: string | null;
  createdAt: string;
}

export interface FrameworkChangeSetDetail {
  changeSet: FrameworkChangeSet;
  items: FrameworkChangeItem[];
  impact: {
    draftProjectsAffected: Array<{ id: string; name: string }>;
    lockedProjectsCount: number;
  };
}

export interface CurrentFrameworkVersion {
  versionNumber: number | null;
  appliedAt: string | null;
  appliedBy: string | null;
}

export function useCurrentFrameworkVersion() {
  return useQuery({
    queryKey: ['framework-version', 'current'],
    queryFn: () => api.get<CurrentFrameworkVersion>('/admin/framework/change-sets/version/current'),
  });
}

export function useFrameworkChangeSets(status?: string) {
  return useQuery({
    queryKey: ['framework-change-sets', { status }],
    queryFn: () =>
      api.get<{ changeSets: FrameworkChangeSet[] }>('/admin/framework/change-sets', {
        status: status || undefined,
      }),
  });
}

export function useFrameworkChangeSet(id: string | null) {
  return useQuery({
    queryKey: ['framework-change-sets', id],
    queryFn: () => api.get<FrameworkChangeSetDetail>(`/admin/framework/change-sets/${id}`),
    enabled: !!id,
    retry: false, // Don't retry — NOT_FOUND is expected after discard
    meta: { suppressErrorToast: true }, // Suppress global error toast for this query
  });
}

export function useCreateChangeSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ changeSet: FrameworkChangeSet }>('/admin/framework/change-sets', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['framework-change-sets'] });
    },
  });
}

export function useSubmitChangeSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title, description }: { id: string; title?: string; description?: string }) =>
      api.post(`/admin/framework/change-sets/${id}/submit`, { title, description }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['framework-change-sets'] });
    },
  });
}

export function useApproveChangeSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/admin/framework/change-sets/${id}/approve`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['framework-change-sets'] });
      queryClient.invalidateQueries({ queryKey: ['workstreams'] });
      queryClient.invalidateQueries({ queryKey: ['skills'] });
      queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
    },
  });
}

export function useRejectChangeSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/admin/framework/change-sets/${id}/reject`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['framework-change-sets'] });
    },
  });
}

export function useDiscardChangeSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/admin/framework/change-sets/${id}/discard`, {}),
    onSuccess: (_data, id) => {
      // Remove the specific change set from cache BEFORE invalidating the list,
      // so the detail query doesn't refetch a deleted change set and trigger NOT_FOUND.
      queryClient.removeQueries({ queryKey: ['framework-change-sets', id] });
      queryClient.invalidateQueries({ queryKey: ['framework-change-sets'] });
    },
  });
}

// ============ Change Items ============

export type FrameworkEntityType =
  | 'workstream'
  | 'capability'
  | 'activity'
  | 'dependency'
  | 'guardrail'
  | 'skill'
  | 'role'
  | 'multiplier'
  | 'allocation'
  | 'start_week';

export interface AddChangeItemInput {
  changeSetId: string;
  changeType: 'create' | 'update' | 'delete';
  entityType: FrameworkEntityType;
  entityId?: string;
  beforeJson?: string;
  afterJson?: string;
}

export function useAddChangeItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ changeSetId, ...body }: AddChangeItemInput) =>
      api.post<{ item: FrameworkChangeItem }>(`/admin/framework/change-sets/${changeSetId}/items`, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['framework-change-sets', variables.changeSetId] });
      queryClient.invalidateQueries({ queryKey: ['framework-change-sets'] });
    },
  });
}

export function useRemoveChangeItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ changeSetId, itemId }: { changeSetId: string; itemId: string }) =>
      api.delete(`/admin/framework/change-sets/${changeSetId}/items/${itemId}`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['framework-change-sets', variables.changeSetId] });
      queryClient.invalidateQueries({ queryKey: ['framework-change-sets'] });
    },
  });
}

// ============ Versions + Restore ============

export interface FrameworkVersionListItem {
  id: string;
  versionNumber: number;
  appliedAt: string;
  appliedBy: string;
  changeSetId: string | null;
  changeSetTitle: string | null;
  changeSetDescription: string | null;
  itemCount: number;
  notes: string | null;
}

export interface FrameworkVersionDetail {
  version: FrameworkVersionListItem;
  /** Full snapshot — null for legacy versions captured before snapshot_json was added */
  snapshot: Record<string, unknown> | null;
}

export function useFrameworkVersions() {
  return useQuery({
    queryKey: ['framework-versions'],
    queryFn: () => api.get<{ versions: FrameworkVersionListItem[] }>('/admin/framework/change-sets/versions/list'),
  });
}

export function useFrameworkVersion(id: string | null) {
  return useQuery({
    queryKey: ['framework-versions', id],
    queryFn: () => api.get<FrameworkVersionDetail>(`/admin/framework/change-sets/versions/${id}`),
    enabled: !!id,
  });
}

export function useRestoreFrameworkVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ changeSetId: string; itemCount: number; targetVersion: number }>(
        `/admin/framework/change-sets/versions/${id}/restore`,
        {}
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['framework-change-sets'] });
    },
  });
}

/**
 * Helper: build a Map<entityId, FrameworkChangeItem> for a given entity type from the
 * current change set's items, so callers can quickly check whether an entity has a
 * pending create/update/delete and render it accordingly.
 */
export function buildPendingMap(
  items: FrameworkChangeItem[] | undefined,
  entityType: FrameworkEntityType
): Map<string, FrameworkChangeItem> {
  const map = new Map<string, FrameworkChangeItem>();
  if (!items) return map;
  for (const item of items) {
    if (item.entityType !== entityType) continue;
    if (item.entityId) {
      map.set(item.entityId, item);
    }
  }
  return map;
}

/**
 * Generate a stable client-side ID for a new entity. Used when creating an entity
 * inside a pending change set so subsequent items can reference the new entity by id
 * without waiting for a server round-trip. The ID is included in the change item's
 * afterJson so applyChangeItem inserts the row with this exact id at approval time.
 */
export function generateEntityId(prefix: string): string {
  const rand = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 16)
    : Math.random().toString(36).slice(2, 18);
  return `${prefix}_${rand}`;
}

/**
 * Record a framework mutation as a change item, with smart dedup of stacked changes:
 *   - Editing the same entity twice → ONE pending UPDATE item (the latest state wins)
 *   - Editing a pending CREATE → keeps it as a CREATE with the new state (no UPDATE on a not-yet-existing row)
 *   - Deleting a pending CREATE → removes the CREATE entirely (no orphan apply)
 *   - Deleting a pending UPDATE → replaces the UPDATE with a DELETE
 *
 * The reason this lives in a hook (not pure function) is that it needs the existing
 * pending items list and the add/remove mutations.
 */
export interface RecordChangeInput {
  entityType: FrameworkEntityType;
  entityId: string | null;
  changeType: 'create' | 'update' | 'delete';
  beforeJson?: string;
  afterJson?: string;
}

export function useRecordChange(
  changeSetId: string | null,
  pendingItems: FrameworkChangeItem[] | undefined
) {
  const addItem = useAddChangeItem();
  const removeItem = useRemoveChangeItem();

  const recordChange = async (input: RecordChangeInput) => {
    if (!changeSetId) throw new Error('Cannot record change: not in edit mode');

    const items = pendingItems || [];
    const existing = input.entityId
      ? items.find((it) => it.entityType === input.entityType && it.entityId === input.entityId)
      : undefined;

    // Special case 1: deleting a pending CREATE → just remove the create item
    if (existing && existing.changeType === 'create' && input.changeType === 'delete') {
      await removeItem.mutateAsync({ changeSetId, itemId: existing.id });
      return;
    }

    // Special case 2: updating a pending CREATE → replace the create with new state
    if (existing && existing.changeType === 'create' && input.changeType === 'update') {
      await removeItem.mutateAsync({ changeSetId, itemId: existing.id });
      await addItem.mutateAsync({
        changeSetId,
        changeType: 'create',
        entityType: input.entityType,
        entityId: input.entityId || undefined,
        beforeJson: undefined,
        afterJson: input.afterJson,
      });
      return;
    }

    // Default: replace any existing item (so stacked updates collapse to one)
    if (existing) {
      await removeItem.mutateAsync({ changeSetId, itemId: existing.id });
    }

    await addItem.mutateAsync({
      changeSetId,
      changeType: input.changeType,
      entityType: input.entityType,
      entityId: input.entityId || undefined,
      beforeJson: input.beforeJson,
      afterJson: input.afterJson,
    });
  };

  /**
   * Undo any pending change for the given entity. Removes the existing change item
   * (if any) so the entity reverts to its live state. Used for "Undo" buttons on
   * pending-delete and pending-modified rows.
   */
  const undoChange = async (entityType: FrameworkEntityType, entityId: string) => {
    if (!changeSetId) return;
    const items = pendingItems || [];
    const existing = items.find((it) => it.entityType === entityType && it.entityId === entityId);
    if (!existing) return;
    await removeItem.mutateAsync({ changeSetId, itemId: existing.id });
  };

  return {
    recordChange,
    undoChange,
    isPending: addItem.isPending || removeItem.isPending,
  };
}
